import { StepDef } from "../models.js";
import { configCheckerOnErrorCallback } from '../configChecker_helpers';
import { getMlConfig } from '../configState.js';
import helpers from "../helpers.js";
import { MotionMonitor, motionMonitorErrors } from '../models.js';
import cc_widgets from '../configChecker_widgets.js';
import immediateApi from "../../../api/immediateApi";


const SILENT_BRAKE_UNLOCK = false; // dev debug
const DEFAULT_SPEED = 50;
const DEFAULT_ACCELERATION = 200;

// -------------------
// Common to all tests
// -------------------

const setPace = (actuatorUuid, speed = DEFAULT_SPEED, acceleration = DEFAULT_ACCELERATION) => {
  immediateApi.setSpeed(actuatorUuid, speed).catch(configCheckerOnErrorCallback);
  immediateApi.setAcceleration(actuatorUuid, acceleration).catch(configCheckerOnErrorCallback);
};

const askNumberOfMotors = (pGoodAnswerCallback) => new StepDef(({ actuator, notifier, diagnose }) => {
  cc_widgets.StepWidgets.askChoice({ title: `How many motors are installed on actuator "${actuator.parameters.friendlyName}"?` }, ['1', '2', '3', '4'], (answer) => {
    answer = parseInt(answer);
    const lTrueNumOfDrives = actuator.children.length + 1;
    if (answer == lTrueNumOfDrives) {
      pGoodAnswerCallback();
    }
    else if (answer > lTrueNumOfDrives) {
      diagnose({
        error: `The number of configured drives does not correspond to the number of installed motors.`,
        resolution: {
          // TODO AUDREY (wording) : the existing numbers should also be off, we should prompt to verify that...
          description: `Only ${lTrueNumOfDrives} drive(s) configured, please add ${answer - lTrueNumOfDrives} drive(s) in the drive settings menu.`,
          type: 'config'
        },
        errorCode: -1,
      });
      notifier.finished();
    }
    else {
      immediateApi.isMotorDetected(actuator.parameters.uuid)
        .then((pData) => {
          const lUndetectedMotors = actuator.getAllActuatorDriveNumbers().filter(driveNumber => pData[driveNumber] === false);
          let message = "";
          if (lUndetectedMotors.length == (lTrueNumOfDrives - answer)) {
            message = `Too many drives configured on "${actuator.parameters.friendlyName}", please remove drive number(s) ${helpers.punctuate(lUndetectedMotors)} from the drive settings menu.`;
          }
          else {
            message = `Too many drives configured on "${actuator.parameters.friendlyName}", please remove ${lTrueNumOfDrives - answer} drive(s) from the drive settings menu.`;
          }
          diagnose({
            error: `The number of configured drives does not correspond to the number of installed motors.`,
            resolution: {
              description: message,
              type: 'config'
            },
            errorCode: -1,
          });
          notifier.finished();
        }).catch(configCheckerOnErrorCallback);
    }
  });
});

const diagnoseUndetectedMotor = new StepDef(({ runtime, actuator, notifier, diagnose }) => {
  if (getMlConfig().getController().isHardware_1D()) {
    diagnose({
      error: `Motor is not connected to the controller.`,
      resolution: {
        description: `Connect the motor to the controller's DRIVE 1 port.`,
        type: 'eStop',
        message: `Connect the motor to the controller's DRIVE 1 port.`
      },
      tooltips: [{category: 'drivePort'}],
      errorCode: -1,
    });
    notifier.finished();
  }
  else {
    runtime.runStep(askNumberOfMotors( () => {
      const missingChildMotor = runtime.ctx.undetectedChild != undefined;
      const missingDriveNumber = missingChildMotor ? runtime.ctx.undetectedChild : actuator.driveNumber;
      cc_widgets.StepWidgets.askChoice({ title: `Is the light of the motor connected to DRIVE ${missingDriveNumber} on?`, tooltips: [{category: 'motorLight'},{category: 'drivePort'}] }, ["Yes", "No", "Not sure"], (answer) => {
        if (answer == 'Yes') {
          diagnose({
            error: `The configured drive number is incorrect.`,
            resolution: {
              description: `Change the drive number ${missingDriveNumber} in the drive settings to match the DRIVE port the${actuator.isMultiDrive() ? missingChildMotor ? " child" : " parent" :  "" } motor is connected to.`,
              type: "config",
            },
            tooltips: [{category: 'drivePort'}],
            errorCode: -1,
          });
          notifier.finished();
          // TODO FUTURE : Could further diagnose here, ask to unplug something so that we can know exactly which port it is plugged into and tell them to unplug it from drive port X and plug it into drive port Y
          // For example, if the actuator has a home/end sensor, and its orange light is on, then we could ask the user to trigger/unplug it.
          // But this seems like a very edge-case...
        }
        else if (answer == 'No') {
          diagnose({
            error: `Motor is not connected to the controller.`,
            resolution: {
              description: `Connect the${actuator.isMultiDrive() ? missingChildMotor ? " child" : " parent" :  "" } motor to the controller's DRIVE ${missingDriveNumber} port.`,
              type: 'eStop',
              message: `Connect the${actuator.isMultiDrive() ? missingChildMotor ? " child" : " parent" :  "" } motor to the controller's DRIVE ${missingDriveNumber} port.`
            },
            tooltips: [{category: 'drivePort'}],
            errorCode: -1,
          });
          notifier.finished();
        }
        else { // Not sure
          diagnose({
            error: `We did not detect the motor that should be connected to DRIVE ${missingDriveNumber} port.`,
            resolution: {
              description: [
                `1. Make sure all your motors are connected to the MachineMotion controller. Press E-Stop before manipulating motor cables.`, // TODO AUDREY : Put better emphasis on estop
                `2. Make sure the drive number as defined in the drive settings matches the DRIVE port the${actuator.isMultiDrive() ? missingChildMotor ? " child" : " parent" :  "" } motor is connected to.`
              ],
              type: "config",
            },
            tooltips: [{category: 'drivePort'}],
            errorCode: -1,
          });
          notifier.finished();
        }
      });
    }));
  }
});

const ensureBrakesUnlocked = (pCompleted) => new StepDef(({ actuator, notifier, runtime, diagnose }) => {
  // Note Aj: The "halt stop" interacts with the brakes.
  // It is good practice to ensure brakes are unlocked before trying to move just in case the last move caused the brakes to lock and then proceeded to never unlock them.
  immediateApi.getBrakeState(actuator.parameters.uuid)
    .then(({status}) => {
      if (status === "Unlocked") { return runtime.runStep(pCompleted); }
      else {
        let unlockBrakesFunc = (callback) => {
          immediateApi.unlockBrakes(actuator.parameters.uuid).then(callback).catch(configCheckerOnErrorCallback);
        };
        let message1 = `Do you have${actuator.isMultiDrive() ? " a brake" : " brake(s)"} installed on actuator "${actuator.parameters.friendlyName}"?`;
        let message2 = "Is it safe to unlock the brake(s)?";

        // Overwrite the message for the drive number test.
        // As we are not sure about the drive numbers are correct, we might have consequences on more than just our actuator.
        // In doubt, unlock all brakes.
        if (runtime.ctx.testName == "driveNumber") {
          message1 = `Do you have brake(s) installed on your machine?`;
          message2 = "Is it safe to unlock all brakes?";
          unlockBrakesFunc = (callback) => {
            immediateApi.unlockAllBrakes(actuator.parameters.controllerId).then(callback).catch(configCheckerOnErrorCallback);
          };
        }

        if (SILENT_BRAKE_UNLOCK) { // DEV ONLY
          console.warn("silently unlocking brakes (DEV MODE)");
          unlockBrakesFunc(() => { return runtime.runStep(pCompleted); });
        }

        cc_widgets.StepWidgets.askPolar({ title: message1, tooltips: [{category: 'brake'}] }, answer => {
          if (answer) {
            cc_widgets.StepWidgets.askChoice(
              {
                title: message2,
                description: `We need to unlock the brake(s) in order to move the actuator.`
              },
              ["No", `Yes, unlock brake(s)`],
              answer => {
                if (answer === "No") {
                  diagnose({
                    error: 'Cannot continue with brakes locked.',
                    resolution : {
                      description: `Make sure it is safe to unlock your brake(s) and click 'Yes, unlock brake(s)' when re-running this step.`,
                    },
                    errorCode: -1,
                    tooltips: [{category: 'brake'}],
                  });
                  notifier.finished();
                } else {
                  unlockBrakesFunc(() => { return runtime.runStep(pCompleted); });
                }
              });
          } else {
            unlockBrakesFunc(() => { return runtime.runStep(pCompleted); });
          }
        });
      }
    })
    .catch(configCheckerOnErrorCallback);
});

const testSetup = (pFirstStep) => new StepDef(({ actuator, runtime }) => {
  immediateApi.isMotorDetected(actuator.parameters.uuid)
    .then((pData) => {
      const hasUndetectedMotor = Object.values(pData).some(isDetected => isDetected === false);
      if (hasUndetectedMotor) {
        // parent is undetected;
        if (!pData[actuator.driveNumber]) {
          runtime.runStep(diagnoseUndetectedMotor);
        }
        // child is undetected;
        else {
          const undetectedChild = actuator.getAllChildDriveNumbers().find(childDriveNumber => pData[childDriveNumber] === false);
          runtime.ctx.undetectedChild = undetectedChild;
          runtime.runStep(diagnoseUndetectedMotor);
        }
      }
      else {
        const startTest = () => {
          setPace(actuator.parameters.uuid);
          runtime.runStep(setInitialState(pFirstStep));
        };

        if (runtime.ctx.testName == 'driveNumber' || runtime.ctx.testName == 'direction' || !actuator.isMultiDrive()) {
          startTest();
        }
        else {
          runtime.runStep(verifyChildrenEndstops(startTest));
        }
      }
    }).catch(configCheckerOnErrorCallback);
});


// ------------------------------------------------
// For drive, direction and end-stop sensors tests
// ------------------------------------------------

const NOTICEABLE_DISTANCE_THRESH = 50;
const DID_MOVE_THRESH = 5;

function hasCoveredMinDistance(runtime, actuator, callback) {
  immediateApi.getAllPositionsForController(actuator.parameters.controllerId)
    .then((pPositions) => {
      const trueAxis = helpers.convert(actuator.driveNumber).toLetter().toUpperCase();
      const distanceTraveled = Math.abs(pPositions[trueAxis] - runtime.ctx.initialPosition[trueAxis]);
      const wasNoticeable = distanceTraveled > NOTICEABLE_DISTANCE_THRESH;
      runtime.ctx.actuatorDidntMove = distanceTraveled < DID_MOVE_THRESH;
      callback(wasNoticeable);
    }).catch(configCheckerOnErrorCallback);
}

const setInitialState = (pNextStep) => new StepDef( ({ actuator, runtime }) => {
  immediateApi.getEndstopState(actuator.parameters.controllerId)
    .then((pEndStopState) => {
      runtime.ctx.initialEndStopStates = pEndStopState;
      immediateApi.getAllPositionsForController(actuator.parameters.controllerId)
        .then((pPositions) => {
          runtime.ctx.initialPosition = pPositions;
          runtime.runStep(pNextStep);
        }).catch(configCheckerOnErrorCallback);
    }).catch(configCheckerOnErrorCallback);
});


// -----------------------------
// For drive and direction tests
// -----------------------------

function getBlindMoveSetup(testName, getExitSequence) {
  const JOG_DISTANCE = 100; // mm
  const CORRECTION_JOG = 20;

  const before = new StepDef( ({ runtime }) => {
    runtime.ctx.testName = testName;
    runtime.runStep(testSetup(chooseMoveDirection));
  });

  const chooseMoveDirection = new StepDef( ({actuator, runtime}) => {
    if(runtime.ctx.initialEndStopStates[actuator.driveNumber].home == true) {
      runtime.ctx.jog = JOG_DISTANCE;
    }
    else {
      runtime.ctx.jog = -JOG_DISTANCE;
    }
    runtime.runStep(ensureBrakesUnlocked(blindMove));
  });

  const blindMove = new StepDef( ({actuator, runtime}) => {
    const shouldPromptMessageIncludeDirection = ( testName != "driveNumber" );
    let promptMessage = `Press "Go" to move the actuator "${actuator.parameters.friendlyName}" by ${JOG_DISTANCE}mm` + (shouldPromptMessageIncludeDirection ? ` towards the ${runtime.ctx.jog > 0 ? "end" : "home" } sensor.` : `.`);

    if (runtime.ctx.isRetrying) {
      if (runtime.ctx.actuatorDidntMove) { promptMessage = 'The actuator failed to move. ' + promptMessage.slice(0,-1) + ' in the other direction.'; }
      else { promptMessage = 'The actuator was stopped before completing the motion. ' + promptMessage; }
    }

    const lInfoWidget = new cc_widgets.InfoWidget();
    const lWatchMachineWidget = new cc_widgets.WatchMachineWidget();
    const consentUI = cc_widgets.StepWidgets.consentMove(
      {
        title: promptMessage,
      },
      () => {
        lInfoWidget.update("Preparing to move", true);
        lWatchMachineWidget.show();

        let motionMonitorError;
        const monitor = new MotionMonitor(actuator, runtime.ctx.initialPosition, runtime.ctx.jog, (data) => {motionMonitorError = data;} );

        setTimeout(()=>{
          lInfoWidget.update("Moving the actuator", true);
          immediateApi.moveRelative(actuator.parameters.uuid, runtime.ctx.jog, true, false)
            .then(() => {
              monitor.start();
              helpers.loop((repeat) => {
                immediateApi.isMotionComplete(actuator.parameters.controllerId)
                  .then(({isComplete}) => {
                    immediateApi.getEndstopState(actuator.parameters.controllerId)
                      .then((pEndStopState) => {

                        if(motionMonitorError != undefined) {
                          lWatchMachineWidget.hide();
                          immediateApi.halt(actuator.parameters.uuid).catch(() => console.error('Failed to halt during get blind move setup.'));
                          monitor.kill();
                          lInfoWidget.update("Motion impeded.", false);
                          if (motionMonitorError == motionMonitorErrors.badDirection) { runtime.ctx.motorsMovingInBadDirection = true; }
                          return runtime.runStep(postMove);
                        }

                        if(isComplete) {
                          lWatchMachineWidget.hide();
                          monitor.kill();
                          lInfoWidget.update("Motion completed.", false);
                          return runtime.runStep(postMove);
                        }

                        for (const driveNumber in pEndStopState) {
                          for (const sensor in pEndStopState[driveNumber]) {
                            if (pEndStopState[driveNumber][sensor] && !runtime.ctx.initialEndStopStates[driveNumber][sensor]) {
                              lWatchMachineWidget.hide();
                              immediateApi.halt(actuator.parameters.uuid).catch(() => console.error('Failed to halt during get blind move setup.'));
                              monitor.kill();
                              lInfoWidget.update("Motion triggered sensor.", false);
                              return runtime.runStep(postMove);
                            }
                          }
                        }

                        repeat();

                      }).catch(configCheckerOnErrorCallback);
                  }).catch(configCheckerOnErrorCallback);
              } , { delay: 250 });
            }).catch(configCheckerOnErrorCallback);
        }, 1500);
      });
    consentUI.addWidget(lWatchMachineWidget);
    consentUI.addWidget(lInfoWidget);
    lWatchMachineWidget.hide();
    lInfoWidget.update("The motion will start 1 second after pressing go. Please watch your machine.", false);
  });

  const postMove = new StepDef( ({runtime, actuator}) => {
    function retryInOppositeDirection(){
      runtime.ctx.isRetrying = true;
      runtime.ctx.jog = -1 * runtime.ctx.jog;
      return runtime.runStep(setInitialState(ensureBrakesUnlocked(blindMove)));
    }

    // if any motor moves in the opposite direction run movementImpededDiagnosis
    if (runtime.ctx.motorsMovingInBadDirection) {
      delete runtime.ctx.motorsMovingInBadDirection;
      return runtime.runStep(movementImpededDiagnosis);
    }

    hasCoveredMinDistance(runtime, actuator, (wasNoticeable) => {

      if (wasNoticeable) {
        return runtime.runStep(getExitSequence(setInitialState(chooseMoveDirection)));
      }
      else if (runtime.ctx.isRetrying) {
        return runtime.runStep(movementImpededDiagnosis);
      }
      else {
        retryInOppositeDirection();
      }
    });
  });

  const movementImpededDiagnosis = new StepDef( ({actuator, runtime, diagnose, fail, notifier}) => {
    if (getMlConfig().getController().isHardware_1D()) {
      fail({
        message: `Actuator "${actuator.parameters.friendlyName}" may be overloaded, or not properly installed or configured.`,
        errorCode: -1,
      });
      notifier.finished();
    }
    else {
      runtime.runStep(askNumberOfMotors( () => {
        if (actuator.isMultiDrive()) {
          // Note : This is a biaised question, people will have a tendency to reply "yes". An unbiased question would be to let them select the numbers.
          cc_widgets.StepWidgets.askChoice({ title: `Are the motors installed on actuator "${actuator.parameters.friendlyName}" connected to the controller ports DRIVE ${helpers.punctuate(actuator.getAllActuatorDriveNumbers())}?`, group: true, tooltips: [{category: 'drivePort'}] }, ["Yes", "No"], (answer) => {
            if(answer === 'Yes') {
              return runtime.runStep(fixDirection);
            }
            else {
              diagnose({
                error: `The configured drive numbers do not correspond to the motor wiring.`,
                resolution: {
                  description: `In the "Drive number" drop-down menu, change the drive numbers to match the controller DRIVE ports that the motors are wired to.`,
                  type: "config",
                },
                tooltips: [{category: 'drivePort'}],
                errorCode: -1,
              });
              notifier.finished();
            }
          });
        }
        else {
          fail({
            message: `Actuator "${actuator.parameters.friendlyName}" may be overloaded, or not properly installed or configured.`,
            errorCode: -1,
          });
          notifier.finished();
        }
      }));
    }
  });

  const fixDirection = new StepDef(({ actuator, runtime }) => {
    runtime.ctx.allPossibleCombinations = helpers.getPermutationsWithRepetition(actuator.children.length);
    runtime.ctx.currentCombinationIndex = 0;

    // Remove current config
    const currentConfig = [];
    actuator.children.forEach(child => {currentConfig.push(child.rotation);});
    runtime.ctx.allPossibleCombinations.splice(runtime.ctx.allPossibleCombinations.findIndex((e) => { return helpers.isArraysEqual(currentConfig, e); }), 1);

    const lInfoWidget = new cc_widgets.InfoWidget();
    const consentUI = cc_widgets.StepWidgets.consentMove(
      {
        title: `The Configuration Checker will try to automatically fix the directions of your motors.`,
      }, () => {
        runtime.runStep(loopConfigs);
        consentUI.addWidget(lInfoWidget);
        lInfoWidget.update('Testing different direction combinations...', true);
      });
  });

  const loopConfigs = new StepDef(({runtime, actuator, fail}) => {
    if (runtime.ctx.currentCombinationIndex >= runtime.ctx.allPossibleCombinations.length) {
      fail({
        message: `The Configuration Checker could not find a valid direction combination. Actuator "${actuator.parameters.friendlyName}" may be overloaded, or not properly installed or configured.`,
        errorCode: -1,
      });
    } else {
      const rotations = runtime.ctx.allPossibleCombinations[runtime.ctx.currentCombinationIndex];
      const mlConfig = getMlConfig();
      const lAxisConfigObj = mlConfig.getAxisByUuid(actuator.parameters.uuid);
      console.log('currently testing', lAxisConfigObj.mRotation, rotations);
      lAxisConfigObj.mChildrenDrives.forEach((child, index) => {
        child.rotation = rotations[index];
        actuator.children[index].rotation = rotations[index];
      });
      mlConfig.getConfigManager().mDirty = true;
      mlConfig.getConfigManager().deploy();

      runtime.runStep(tryMovePositive);
    }
  });

  const tryMovePositive = new StepDef( ({actuator, runtime}) => {
    immediateApi.getSmartDriveStatus(actuator.parameters.controllerId)
      .then(() => {
        console.log('Trying to move positive');
        runtime.ctx.correctionJog = CORRECTION_JOG;

        function pFail(){
          return runtime.runStep(tryMoveNegative);
        }
        return runtime.runStep(ensureBrakesUnlocked(tryMove(pFail)));
      }).catch(configCheckerOnErrorCallback);
  });

  const tryMoveNegative = new StepDef( ({actuator, runtime}) => {
    immediateApi.getSmartDriveStatus(actuator.parameters.controllerId)
      .then(() => {
        console.log('Trying to move negative');
        runtime.ctx.correctionJog = -1 * CORRECTION_JOG;

        function pFail(){
          runtime.ctx.currentCombinationIndex = runtime.ctx.currentCombinationIndex + 1 ;
          return runtime.runStep(loopConfigs);
        }
        return runtime.runStep(ensureBrakesUnlocked(tryMove(pFail)));
      }).catch(configCheckerOnErrorCallback);
  });

  // Question Audrey : I wonder if what is below could not be shared with blindMove...
  // Aj: Blind moves consider sensors and the move below do not. But it still might be possible, but is it necessary ?
  const tryMove = (callbackOnFail) => new StepDef( ({actuator, runtime}) => {
    immediateApi.getAllPositionsForController(actuator.parameters.controllerId)
      .then((pPositions) => {
        runtime.ctx.initialPosition = pPositions;
        let motionMonitorError;
        const monitor = new MotionMonitor(actuator, runtime.ctx.initialPosition, runtime.ctx.correctionJog, (data) => {motionMonitorError = data;} );

        immediateApi.moveRelative(actuator.parameters.uuid, runtime.ctx.correctionJog, true, false)
          .then(() => {
            monitor.start();
            helpers.loop((repeat) => {
              immediateApi.isMotionComplete(actuator.parameters.controllerId)
                .then(({isComplete}) => {
                  if(motionMonitorError != undefined) {
                    immediateApi.halt(actuator.parameters.uuid).catch(() => console.error('Failed to halt during get blind move setup.'));
                    monitor.kill();
                    return callbackOnFail();
                  }

                  if(isComplete) {
                    monitor.kill();
                    return runtime.runStep(fixedDirection);
                  }
                  repeat();
                }).catch(configCheckerOnErrorCallback);
            } , { delay: 250 });
          }).catch(configCheckerOnErrorCallback);
      }).catch(configCheckerOnErrorCallback);
  });

  const fixedDirection = new StepDef( ({actuator, autofix, notifier}) => {
    immediateApi.getSmartDriveStatus(actuator.parameters.controllerId)
      .then(() => {
        autofix({
          message: `The Configuration Checker has changed the direction of your motors. Your actuator should now be able to move.`,
          category: 'direction',
        });
        notifier.finished();
      }).catch(configCheckerOnErrorCallback);
  });

  return before;
}

const verifyChildrenEndstops = (pStartTest) => new StepDef(({ actuator, notifier, diagnose }) => {
  immediateApi.getEndstopState(actuator.parameters.controllerId).then((pEndStopState) => {
    let badChildSensor;
    let badChildNumber;

    actuator.getAllChildDriveNumbers().forEach(childDriveNum => {
      if (pEndStopState[childDriveNum].home) {
        badChildSensor = 'home';
        badChildNumber = childDriveNum;
        return;
      }
      if (pEndStopState[childDriveNum].end) {
        badChildSensor = 'end';
        badChildNumber = childDriveNum;
        return;
      }
    });

    if (badChildNumber != undefined) {
      // timeout to add interval between retries, so user has a perception that the test was rerun (when no interval is present, when user hits retry and problem is not
      // resolved the same error message is seen, the user assumes the test did not retry.)
      setTimeout(() => {
        immediateApi.getEndstopConfig(actuator.parameters.controllerId, badChildNumber)
          .then((pEndStopConfig) => {
            diagnose({
              error: "Child motor missing sensor jumper.",
              resolution: {
                description: `Please connect a sensor jumper to the SENSOR ${pEndStopConfig[badChildSensor]} port of the child motor connected to the DRIVE ${badChildNumber} port.`,
              },
              tooltips: [{text: `What is port SENSOR ${pEndStopConfig[badChildSensor]}?`, category: 'sensorPort'}, {category: 'sensorJumper'}],
              errorCode: -1,
            });
            notifier.finished();
            return;
          }).catch(configCheckerOnErrorCallback);
      }, 1500);
      return;
    }
    pStartTest();
  }).catch(configCheckerOnErrorCallback);
});

export {
  testSetup,
  setInitialState,
  ensureBrakesUnlocked,
  getBlindMoveSetup,
  hasCoveredMinDistance,
  verifyChildrenEndstops,
};
